import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
	InfoOutlined,
	CheckCircleOutlined,
	BarChart,
	ChevronLeftOutlined,
	ChevronRightOutlined,
	DescriptionOutlined,
} from '@mui/icons-material';
import {
	ConfirmModal,
	EmptyState,
	FormActions,
	FormCard,
	FormSectionTitle,
	LoadingState,
	PageHeader,
	TabBar,
	useConfirmModal,
	useToast,
} from '@practium/ui';
import { useGetDefensives, useGetFertilizers } from '@practium/features/api';
import {
	useGetAdviceMaintenanceNutritionTable,
	useGetAdviceMaintenanceReport,
} from '../../api/hooks/maintenance';
import type { FertilizationPlanAdvice, MaintenanceWorkspaceTab } from '../types';
import { MaintenanceForm } from '../components/Forms/MaintenanceForm/MaintenanceForm';
import { ExecutionMonthEmpty } from '../components/Tables/ExecutionTable/ExecutionMonthEmpty';
import { ExecutionTable } from '../components/Tables/ExecutionTable/ExecutionTable';
import { SimpleDataTable } from '../components/Tables/SimpleDataTable/SimpleDataTable';
import { PlanningPanel } from '../components/Planning/PlanningPanel';
import { MixturesPanel } from '../components/Mixtures/MixturesPanel';
import { areMixturesConfirmed, getPlanningMethod } from '../utils/mixturesMapper';
import { MonthPicker } from '../components/MonthPicker/MonthPicker';
import { useMaintenanceStore } from '../store/maintenanceStore';
import {
	formatExecutionPeriodTitle,
	formatPeriodLabel,
	MAINTENANCE_TITLES,
	MAINTENANCE_SUBTITLES,
	WORKSPACE_INITIAL_TABS,
	WORKSPACE_TABS,
} from '../constants/maintenance.constants';
import { mapMaintenanceAnnualNutritionRows } from '../utils/maintenanceNutritionTableMapper';
import {
	isUpcomingPeriod,
	mapMaintenanceExecutionPeriod,
} from '../utils/maintenanceExecutionMapper';
import { getMaintenanceMonths } from '../utils/maintenanceAdviceMapper';
import { fromApiPeriod } from '../utils/planningTableMapper';
import {
	ReportViewer,
	type ReportViewerHandle,
} from '../components/Reports/ReportViewer/ReportViewer';
import { ReportActionButtons } from '../components/Reports/ReportActionButtons/ReportActionButtons';
import { PanelActions } from '../components/PanelActions/PanelActions';
import { RecommendationSubtitle } from '../components/RecommendationSubtitle/RecommendationSubtitle';
import styles from './MaintenanceWorkspacePage.module.css';

export interface MaintenanceWorkspacePageProps {
	viewData: FertilizationPlanAdvice;
	onBack: () => void;
	onEdit?: () => void;
	onDelete?: () => Promise<void> | void;
	initialTab?: MaintenanceWorkspaceTab;
	onTabChange?: (tab: MaintenanceWorkspaceTab) => void;
}

interface GeneralPanelProps {
	viewData: FertilizationPlanAdvice;
	onEdit?: () => void;
	onDelete?: () => Promise<void> | void;
	onDeleteConfirm: () => void;
}

const GeneralPanel = ({ viewData, onEdit, onDelete, onDeleteConfirm }: GeneralPanelProps) => (
	<div className={styles.panelContent}>
		<FormCard icon={<InfoOutlined />} title="Informações Gerais">
			<MaintenanceForm mode="view" viewData={viewData} onSubmit={() => {}} />
		</FormCard>
		<PanelActions>
			<FormActions
				mode="view"
				onCancel={() => {}}
				onSave={() => {}}
				onDelete={onDelete ? onDeleteConfirm : undefined}
				onEdit={onEdit}
			/>
		</PanelActions>
	</div>
);

interface ExecutionPanelProps {
	advice: FertilizationPlanAdvice;
	onRegister: () => void;
}

const ExecutionPanel = ({ advice, onRegister }: ExecutionPanelProps) => {
	const { data: fertilizers = [], isLoading: isLoadingFertilizers } = useGetFertilizers();
	const { data: defensives = [], isLoading: isLoadingDefensives } = useGetDefensives();
	const products = useMemo(
		() => [
			...fertilizers,
			...defensives,
			...(advice.usedFertilizers ?? []).filter(
				(used) =>
					!fertilizers.some((product) => product._id === used._id) &&
					!defensives.some((product) => product._id === used._id)
			),
		],
		[advice.usedFertilizers, defensives, fertilizers]
	);
	const periods = useMemo(() => getMaintenanceMonths(advice.periods), [advice.periods]);
	const tablesByPeriod = useMemo(
		() =>
			Object.fromEntries(
				periods.map((period) => [period, mapMaintenanceExecutionPeriod(advice, products, period)])
			),
		[advice, periods, products]
	);

	const renderContent = () => {
		if (isLoadingFertilizers || isLoadingDefensives) return <LoadingState />;
		if (periods.length === 0) {
			return (
				<EmptyState
					icon={<CheckCircleOutlined />}
					title="Nenhum período encontrado"
					description="Esta recomendação ainda não possui meses de execução."
				/>
			);
		}
		return (
			<div className={styles.executionPeriods}>
				{periods.map((period) => {
					const table = tablesByPeriod[period];
					const hasExecution = (table?.groups.length ?? 0) > 0;

					return (
						<section key={period} className={styles.executionPeriod}>
							<FormSectionTitle>{formatExecutionPeriodTitle(period)}</FormSectionTitle>
							{hasExecution && table ? (
								<ExecutionTable data={table} />
							) : (
								<ExecutionMonthEmpty
									variant={isUpcomingPeriod(period) ? 'upcoming' : 'current'}
									monthLabel={formatPeriodLabel(period).toLocaleLowerCase('pt-BR')}
									onRegister={onRegister}
								/>
							)}
						</section>
					);
				})}
			</div>
		);
	};

	return (
		<div className={styles.panelContent}>
			<FormCard icon={<CheckCircleOutlined />} title="Execução">
				{renderContent()}
			</FormCard>
		</div>
	);
};

interface NutrientDemandPanelProps {
	adviceId: string;
}

const NutrientDemandPanel = ({ adviceId }: NutrientDemandPanelProps) => {
	const { data, isLoading, isError } = useGetAdviceMaintenanceNutritionTable(adviceId);
	const groups = mapMaintenanceAnnualNutritionRows(data);

	const renderContent = () => {
		if (isLoading) return <LoadingState />;
		if (isError) {
			return (
				<EmptyState
					icon={<BarChart />}
					title="Não foi possível carregar a demanda atendida"
					description="Tente novamente mais tarde."
				/>
			);
		}
		if (groups.length === 0) {
			return (
				<EmptyState
					icon={<BarChart />}
					title="Demanda atendida indisponível"
					description="Ainda não há dados nutricionais para esta recomendação."
				/>
			);
		}
		return (
			<div className={styles.nutrientStatusGroups}>
				{groups.map((group) => (
					<div key={group.title} className={styles.nutrientStatusGroup}>
						<FormSectionTitle>{group.title}</FormSectionTitle>
						<SimpleDataTable columns={group.columns} rows={group.rows} />
					</div>
				))}
			</div>
		);
	};

	return (
		<div className={styles.panelContent}>
			<FormCard icon={<BarChart />} title="Status da Adubação">
				{renderContent()}
			</FormCard>
		</div>
	);
};

interface ReportPanelProps {
	adviceId: string;
	periods: string[];
	activeMonth: string;
	onMonthChange: (month: string) => void;
}

const reportIssuedDateFormatter = new Intl.DateTimeFormat('pt-BR');

const ReportPanel = ({ adviceId, periods, activeMonth, onMonthChange }: ReportPanelProps) => {
	const { data, isLoading, isFetching, isError } = useGetAdviceMaintenanceReport(
		adviceId,
		activeMonth
	);
	const viewerRef = useRef<ReportViewerHandle>(null);

	const currentIndex = periods.indexOf(activeMonth);
	const canGoPrevious = currentIndex > 0;
	const canGoNext = currentIndex >= 0 && currentIndex < periods.length - 1;

	const periodByPickerValue = useMemo(() => {
		const map = new Map<string, string>();
		periods.forEach((period) => map.set(fromApiPeriod(period), period));
		return map;
	}, [periods]);

	const pickerMonths = useMemo(() => periods.map(fromApiPeriod), [periods]);
	const pickerValue = fromApiPeriod(activeMonth);

	const handlePickerChange = (nextPickerValue: string) => {
		const original = periodByPickerValue.get(nextPickerValue);
		if (original) onMonthChange(original);
	};

	return (
		<div className={styles.panelContent}>
			<FormCard icon={<DescriptionOutlined />} title="Relatório Mensal">
				<div className={styles.reportToolbar}>
					<div className={styles.reportNav}>
						<button
							type="button"
							className={styles.navButton}
							onClick={() => canGoPrevious && onMonthChange(periods[currentIndex - 1])}
							disabled={!canGoPrevious}
							aria-label="Mês anterior"
						>
							<ChevronLeftOutlined className={styles.navIcon} />
						</button>
						<button
							type="button"
							className={styles.navButton}
							onClick={() => canGoNext && onMonthChange(periods[currentIndex + 1])}
							disabled={!canGoNext}
							aria-label="Próximo mês"
						>
							<ChevronRightOutlined className={styles.navIcon} />
						</button>
						<div className={styles.reportMonthPicker}>
							<MonthPicker
								value={pickerValue}
								onChange={handlePickerChange}
								allowedMonths={pickerMonths}
								showYear
								triggerClassName={styles.reportMonthPickerTrigger}
							/>
						</div>
					</div>
					<ReportActionButtons
						onPrint={() => viewerRef.current?.print()}
						onDownload={() =>
							viewerRef.current?.download(`relatorio-manutencao-${activeMonth}.pdf`)
						}
						disabled={!data}
					/>
				</div>
				<div className={styles.reportMeta}>
					<span className={styles.reportMetaDate}>
						Emitido em {reportIssuedDateFormatter.format(new Date())}
					</span>
					<span className={styles.reportMetaCount}>
						{' '}
						· {currentIndex >= 0 ? currentIndex + 1 : 0} de {periods.length} meses com relatório
					</span>
				</div>
				<ReportViewer
					ref={viewerRef}
					data={data}
					isLoading={isLoading || isFetching}
					isError={isError}
					documentLabel={`Relatório de manutenção - ${activeMonth}`}
					emptyState={
						!activeMonth ? (
							<EmptyState
								icon={<DescriptionOutlined />}
								title="Selecione um período"
								description="Selecione um mês para montar o relatório."
							/>
						) : undefined
					}
				/>
			</FormCard>
		</div>
	);
};

export const MaintenanceWorkspacePage = ({
	viewData,
	onBack,
	onEdit,
	onDelete,
	initialTab,
	onTabChange,
}: MaintenanceWorkspacePageProps) => {
	const { activeTab, setActiveTab, reportActiveMonth, setReportActiveMonth } =
		useMaintenanceStore();

	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	const { success: toastSuccess, error: toastError, info: toastInfo } = useToast();

	const handleDelete = useCallback(async () => {
		const confirmed = await confirm({
			title: 'Excluir recomendação',
			message: 'Tem certeza que deseja excluir esta recomendação? Esta ação não pode ser desfeita.',
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});
		if (confirmed) {
			try {
				await onDelete?.();
				toastSuccess('Recomendação excluída com sucesso.');
			} catch (error) {
				toastError(error instanceof Error ? error.message : 'Erro ao excluir recomendação.');
			}
		}
	}, [confirm, onDelete, toastError, toastSuccess]);

	const periods = useMemo(() => getMaintenanceMonths(viewData.periods), [viewData.periods]);
	const defaultMonth = periods[0] ?? '';
	const resolvedReportMonth = periods.includes(reportActiveMonth)
		? reportActiveMonth
		: defaultMonth;

	const planningMethod = getPlanningMethod(viewData);
	const needsMixtures = planningMethod === 'irrigation';
	const isExecutionReady =
		!!planningMethod && (!needsMixtures || areMixturesConfirmed(viewData.mixtures));
	const visibleTabs = useMemo(
		() =>
			WORKSPACE_TABS.filter((tab) => {
				if (WORKSPACE_INITIAL_TABS.includes(tab.key)) return true;
				return tab.key === 'mixtures' ? needsMixtures : isExecutionReady;
			}),
		[isExecutionReady, needsMixtures]
	);
	const currentTab = visibleTabs.some((tab) => tab.key === activeTab) ? activeTab : 'general';

	useEffect(() => {
		setActiveTab(initialTab ?? 'general');
	}, [setActiveTab, viewData._id]);

	const handleTabChange = (tab: MaintenanceWorkspaceTab) => {
		setActiveTab(tab);
		onTabChange?.(tab);
	};

	const renderPanel = (): React.ReactNode => {
		switch (currentTab) {
			case 'general':
				return (
					<GeneralPanel
						viewData={viewData}
						onEdit={onEdit}
						onDelete={onDelete}
						onDeleteConfirm={handleDelete}
					/>
				);
			case 'planning':
				return null;
			case 'mixtures':
				return (
					<MixturesPanel
						key={viewData._id}
						advice={viewData}
						onSaved={() => handleTabChange('execution')}
					/>
				);
			case 'execution':
				return (
					<ExecutionPanel
						advice={viewData}
						onRegister={() =>
							toastInfo('Registro de aplicação ainda não foi implementado.')
						}
					/>
				);
			case 'nutrientDemand':
				return <NutrientDemandPanel adviceId={viewData._id ?? ''} />;
			case 'report':
				return (
					<ReportPanel
						adviceId={viewData._id ?? ''}
						periods={periods}
						activeMonth={resolvedReportMonth}
						onMonthChange={setReportActiveMonth}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<div className={styles.page}>
			<div className={styles.contentWrapper}>
				<PageHeader
					title={MAINTENANCE_TITLES['view']}
					subtitle={
						<RecommendationSubtitle text={MAINTENANCE_SUBTITLES['view']} property={viewData.prop} />
					}
					onBack={onBack}
				/>
				<div className={styles.workspace}>
					<div className={styles.workspaceTabs}>
						<TabBar tabs={visibleTabs} activeTab={currentTab} onTabChange={handleTabChange} />
					</div>
					<div className={styles.workspaceContent}>
						<div className={currentTab === 'planning' ? styles.panelContent : styles.hiddenPanel}>
							<PlanningPanel
								advice={viewData}
								onSaved={(method) =>
									handleTabChange(method === 'irrigation' ? 'mixtures' : 'execution')
								}
							/>
						</div>
						{currentTab !== 'planning' ? renderPanel() : null}
					</div>
				</div>
			</div>

			<ConfirmModal
				isOpen={isConfirmOpen}
				title={confirmOptions.title}
				message={confirmOptions.message}
				confirmText={confirmOptions.confirmText}
				cancelText={confirmOptions.cancelText}
				variant={confirmOptions.variant}
				onConfirm={handleConfirm}
				onCancel={handleConfirmCancel}
			/>
		</div>
	);
};
